import React from 'react';
import styled from '@benzinga/themetron';
import { Button, Icon } from '@benzinga/core-ui';
import { SmartLink } from '@benzinga/analytics';
import { AltOffering, AltOfferingCompany, OfferingAttribute, OfferingTag, OfferingTake } from '@benzinga/alts-manager';
import Image from 'next/image';

import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { sanitizeHTML } from '@benzinga/frontend-utils';
// import { formatTextPrice, formatPercentRange } from '@benzinga/utils';
import { OfferingCardVariant } from '../types';

export interface OfferingCardProps {
  attributes?: OfferingAttribute[];
  getStartedLink?: string | null;
  handleLeadCapture?: () => void;
  link?: string | null;
  onTagClick?: (label: string, value: string) => void;
  platform: AltOfferingCompany;
  primaryImage: string | null;
  profileImage?: string | null;
  shortDescription?: string | null;
  subtitle?: string | null;
  tags: OfferingTag[];
  take?: OfferingTake;
  title: string;
  variant?: OfferingCardVariant;
  slug?: string;
}

export const offeringInvestorType = (offering: AltOffering) =>
  offering.nonAccredited || offering.product?.openToNonAccredited === 'Yes' ? 'Non-Accredited' : 'Accredited';

const tagsHidden = ['Real Estate Crowdfunding'];
export const offeringListingType = (offering: AltOffering) =>
  offering && tagsHidden.includes(offering.type) ? null : offering?.type;

export const offeringLink = (offering: AltOffering) =>
  offering.affiliateLink ??
  offering.product?.affiliateLink ??
  (offering?.slug ? `/alternative-investments/listing/${offering?.slug}` : '');

export const offeringToCardProps = (offering: AltOffering): OfferingCardProps => {
  const tags: OfferingTag[] = [];
  if (offering.funded) {
    tags.push({
      label: 'Fully Funded',
      type: 'Funding Status',
      variant: 'success',
    });
  }
  if (offering.propertyType) {
    tags.push({
      label: offering.propertyType,
      type: 'propertyType',
    });
  }
  const investorType = offeringInvestorType(offering);
  if (investorType) {
    tags.push({
      label: investorType,
      type: 'investorType',
    });
  }
  const listingType = offeringListingType(offering);
  if (listingType) {
    tags.push({
      label: listingType,
      type: 'category',
    });
  }
  const link = offeringLink(offering);

  return {
    attributes: offering.attributes,
    link: link,
    platform: offering.company,
    primaryImage: offering.image ?? offering.product?.image,
    profileImage: offering.product?.profileImage,
    shortDescription: offering.product?.shortDescription,
    slug: offering?.slug,
    subtitle: null,
    tags: tags,
    take: offering.product?.productTake
      ? {
          text: offering.product?.productTake,
          url: offering.product?.productArticle,
        }
      : undefined,
    title: offering.name,
  };
};

export const OfferingCard: React.FC<OfferingCardProps> = ({
  attributes,
  getStartedLink,
  handleLeadCapture,
  link,
  onTagClick,
  platform,
  primaryImage,
  profileImage,
  shortDescription,
  slug,
  subtitle,
  tags,
  take,
  title,
  variant,
}) => {
  const handleClickTag = (label: string, value: string) => {
    onTagClick && onTagClick(label, value);
  };

  return (
    <OfferingCardContainer className={`listing-preview ${variant}`}>
      {variant === 'featured' ? (
        <>
          <div className={`listing-image-container ${profileImage ? 'has-profile-image' : ''}`}>
            {primaryImage && (
              <div>
                <a data-label="Offering Details" href={`/alternative-investments/listing/${slug}`}>
                  <Thumbnail className="card-image">
                    <Image
                      alt=""
                      className="object-fill"
                      fill
                      sizes="(max-width: 800px) 240px, (min-width: 800px) 360px"
                      src={primaryImage}
                    />
                  </Thumbnail>
                </a>
              </div>
            )}
          </div>
          <div className="listing-info-wrap">
            <div className="listing-info-heading-text p-4 flex flex-col justify-between">
              <a href={`/alternative-investments/listing/${slug}`}>
                <h3 className="listing-company text-center">{title}</h3>
              </a>
              {shortDescription && (
                <div
                  className="plat-description text-center mb-4"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(shortDescription) }}
                />
              )}

              <div className="details-bottom">
                {attributes && attributes[0] && (
                  <div className="offering-attr left-attr">
                    <span className="label">{attributes[0].label}</span>
                    <span className="value">{attributes[0].value}</span>
                  </div>
                )}

                {attributes && attributes[1] && (
                  <div className="offering-attr right-attr">
                    <span className="label">{attributes[1].label}</span>
                    <span className="value">{attributes[1].value}</span>
                  </div>
                )}
              </div>

              {getStartedLink && (
                <div className="text-center mt-2">
                  <Button as="a" className="write-an-email-button" href={getStartedLink} size="lg" variant="flat-black">
                    Invest Now
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={`listing-image-container ${profileImage ? 'has-profile-image' : ''}`}>
            {primaryImage && variant !== 'compact' && (
              <a data-label="Offering Details" href={`/alternative-investments/listing/${slug}`}>
                <Thumbnail className="card-image">
                  <Image
                    alt=""
                    className="object-fill"
                    fill
                    sizes="(max-width: 800px) 240px, (min-width: 800px) 360px"
                    src={primaryImage}
                  />
                </Thumbnail>
              </a>
            )}
            {/* Need to add use correct image data */}
            {profileImage && (
              <div className="listing-author-image">
                <Image alt="" className="object-cover" fill sizes="100px" src={profileImage} />
              </div>
            )}
          </div>

          <div className="card-info-container">
            <div className="listing-info">
              <span>
                <div className="listing-info-container">
                  <div className="listing-info-box">
                    <div className="listing-info-heading">
                      {primaryImage && variant === 'compact' && (
                        <a data-label="Offering Details" href={`/alternative-investments/listing/${slug}`}>
                          <Thumbnail className="card-image">
                            <Image
                              alt=""
                              className="object-cover"
                              fill
                              sizes="(max-width: 800px) 80px, (min-width: 800px) 80px"
                              src={primaryImage}
                            />
                          </Thumbnail>
                        </a>
                      )}
                      <div className="listing-info-heading-text">
                        <a data-label="Offering Details" href={`/alternative-investments/listing/${slug}`}>
                          <h4 className="listing-title">{title}</h4>
                        </a>
                        {subtitle && <p className="listing-subtitle">{subtitle}</p>}
                        {platform &&
                          (platform?.slug ? (
                            <a href={`/alternative-investments/platform/${platform?.slug}`}>
                              <div className="listing-company">{platform.name}</div>
                            </a>
                          ) : (
                            <div className="listing-company">{platform.name}</div>
                          ))}
                      </div>
                    </div>
                    <div className="tags-container">
                      {tags &&
                        tags.map((tag: OfferingTag) => (
                          <button
                            className={`offering-tag ${tag.variant ?? 'default'}`}
                            key={tag.label}
                            onClick={() => handleClickTag(tag.type, tag.label)}
                          >
                            {tag.label}
                          </button>
                        ))}
                    </div>

                    <div className="details-bottom">
                      {attributes && attributes[0] && (
                        <div className="offering-attr left-attr">
                          <span className="label">{attributes[0].label}</span>
                          <span className="value">{attributes[0].value}</span>
                        </div>
                      )}

                      {attributes && attributes[1] && (
                        <div className="offering-attr right-attr">
                          <span className="label">{attributes[1].label}</span>
                          <span className="value">{attributes[1].value}</span>
                        </div>
                      )}
                    </div>

                    {variant === 'full' && take && (
                      <div className="product-take">
                        {take.text}
                        {take.url && (
                          <a className="read-more-button" href={take.url} rel="noopener noreferrer" target="_blank">
                            Read More
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </span>
              {getStartedLink ? (
                <SmartLink
                  className="get-started-button"
                  href={getStartedLink}
                  label="Offering Get Started"
                  onClick={handleLeadCapture}
                >
                  Get Offer <Icon icon={faArrowRight} />
                </SmartLink>
              ) : (
                link && (
                  <a
                    className="get-details-button"
                    data-label="Offering Details"
                    href={`/alternative-investments/listing/${slug}`}
                  >
                    Get Details <Icon icon={faArrowRight} />
                  </a>
                )
              )}
            </div>
          </div>
        </>
      )}
    </OfferingCardContainer>
  );
};

const OfferingCardContainer = styled.div`
  &.listing-preview {
    width: 100%;
    overflow: hidden;
    border-radius: ${({ theme }) => theme.borderRadius.default};
    background-color: ${({ theme }) => theme.colorPalette.white};
    border: 1px solid ${({ theme }) => theme.colors.border};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;

    &.featured {
      height: 100%;
      display: flex;
      flex-direction: column;
      .listing-info-wrap {
        height: 100%;
      }
      .offering-attr .value {
        font-size: 16px;
      }
      .listing-info-heading-text {
        height: 100%;
      }
      h3 {
        font-size: 1.6rem;
        font-weight: 500;
      }
    }

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow.sm};
    }

    &.compact {
      .card-image {
        width: 80px;
        min-width: 80px;
        max-width: 80px;
        height: 70px;
        margin-right: 10px;
        border-radius: ${({ theme }) => theme.borderRadius.default};
        border: 1px solid ${({ theme }) => theme.colors.border};
      }

      .card-info-container {
        display: flex;
        flex-direction: column;

        h6 {
          margin-top: -5px;
        }

        .listing-info {
          h4 {
            white-space: normal;
            line-height: 1.3;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
        }
      }

      .tags-container {
        height: auto;
      }
    }

    .listing-image-container {
      position: relative;
      &.has-profile-image {
        margin-bottom: 30px;
      }
      .listing-author-image {
        height: 100px;
        width: 100px;
        position: absolute;
        bottom: -15%;
        border-radius: 1000px;
        left: 20px;
        overflow: hidden;
      }
    }

    .tags-container {
      margin: 10px 0;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;

      .offering-tag {
        font-size: ${({ theme }) => theme.fontSize.sm};
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
        padding: 4px 8px;
        background-color: ${({ theme }) => theme.colorPalette.blue50};
        color: ${({ theme }) => theme.colorPalette.blue500};
        display: inline-block;
        border-radius: ${({ theme }) => theme.borderRadius.default};
        height: 30px;
        &.success {
          color: ${({ theme }) => theme.colorPalette.green500};
          border: 1px solid ${({ theme }) => theme.colorPalette.green500};
          background-color: ${({ theme }) => theme.colorPalette.green50};
        }
      }
    }

    .product-take {
      height: 4rem;
      position: relative;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      font-size: 0.9rem;
      -webkit-line-clamp: 2;
      .read-more-button {
        color: ${({ theme }) => theme.colorPalette.blue500};
        position: absolute;
        top: 2.7rem;
        width: 100%;
        text-align: center;
        left: 0;
        background-color: white;
        &:before {
          content: ' ';
          background: linear-gradient(transparent, white);
          display: block;
          height: 1.5em;
          margin-top: -1.5em;
          position: relative;
        }
      }
    }

    .offering-attr {
      .label {
        color: ${({ theme }) => theme.colorPalette.gray500};
        font-size: ${({ theme }) => theme.fontSize.base};
      }

      .value {
        color: ${({ theme }) => theme.colorPalette.gray700};
        font-size: 20px;
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
      }
    }

    .right-attr {
      text-align: right;
    }

    .card-info-container {
      height: 100%;
      .listing-info {
        margin-top: 5px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: calc(100% - 5px);

        h4 {
          font-size: ${({ theme }) => theme.fontSize.lg};
          font-weight: 700;
          margin-bottom: 0;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          text-transform: uppercase;
          color: ${({ theme }) => theme.colorPalette.gray700};
          max-width: 320px;
        }
        .listing-subtitle {
          font-size: ${({ theme }) => theme.fontSize.lg};
          color: ${({ theme }) => theme.colorPalette.gray900};
          margin: 0;
        }
        h6 {
          font-size: ${({ theme }) => theme.fontSize.lg};
          color: ${({ theme }) => theme.colorPalette.gray500};
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          margin-bottom: 0;
        }

        .listing-company {
          font-size: 0.8rem;
          color: ${({ theme }) => theme.colorPalette.gray500};
        }
      }
    }

    .listing-info-box {
      padding: 10px 15px;
    }

    .listing-info-heading {
      display: flex;

      .listing-info-heading-text {
        overflow: hidden;
      }
    }

    .details-bottom {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;

      > div {
        display: flex;
        flex-direction: column;
      }
    }

    .get-details-button,
    .get-started-button {
      display: flex;
      width: 100%;
      background-color: ${({ theme }) => theme.colorPalette.blue500};
      padding: 10px;
      justify-content: space-between;
      align-items: center;
      color: #ffffff;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }
    .get-details-button {
      background-color: ${({ theme }) => theme.colorPalette.blue500};
    }
    .get-started-button {
      background-color: #fa8418;
    }
    .plat-description {
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 4;
      font-size: 1.1rem;
      line-height: 1.5;
    }
  }
`;

const Thumbnail = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  height: 200px;
  width: 100%;
  overflow: hidden;
  background-color: rgb(217, 217, 217);
`;

// const VerifiedBadgeIcon = () => (
//   <svg
//     className="verified-badge"
//     height="560"
//     version="1.1"
//     viewBox="188.977 0 560 560"
//     width="560"
//     x="0"
//     xmlns="http://www.w3.org/2000/svg"
//     xmlSpace="preserve"
//     y="0"
//   >
//     <path
//       d="M748.977 293.125c0 19.578-4.704 37.734-14.11 54.36s-21.984 29.64-37.844 38.718c.438 2.953.657 7.547.657 13.782 0 29.64-9.953 54.796-29.64 75.578-19.798 20.89-43.642 31.28-71.532 31.28-12.469 0-24.39-2.296-35.657-6.89-8.75 17.938-21.328 32.375-37.843 43.422C506.602 554.531 488.555 560 468.977 560c-20.016 0-38.172-5.36-54.36-16.297-16.297-10.828-28.765-25.375-37.515-43.75-11.266 4.594-23.078 6.89-35.657 6.89-27.89 0-51.843-10.39-71.86-31.28-20.015-20.782-29.968-46.048-29.968-75.579 0-3.281.438-7.875 1.203-13.781-15.86-9.188-28.437-22.094-37.844-38.719-9.296-16.625-14-34.781-14-54.36 0-20.783 5.25-39.924 15.641-57.205s24.39-30.078 41.89-38.39c-4.593-12.47-6.89-25.047-6.89-37.516 0-29.531 9.953-54.797 29.969-75.578s43.969-31.281 71.86-31.281c12.468 0 24.39 2.296 35.656 6.89 8.75-17.937 21.328-32.375 37.843-43.422C431.352 5.575 449.398-.003 468.977-.003s37.625 5.578 54.03 16.516c16.407 11.047 29.095 25.484 37.845 43.422 11.265-4.594 23.078-6.89 35.656-6.89 27.89 0 51.734 10.39 71.531 31.28s29.64 46.047 29.64 75.579c0 13.78-2.077 26.25-6.233 37.515 17.5 8.313 31.5 21.11 41.89 38.39 10.39 17.392 15.64 36.532 15.64 57.316z"
//       fill="#00BCEF"
//     ></path>
//     <path
//       d="M325.72 310.034l30.842-38.964 77.92 61.689 138.79-175.322 38.96 30.843L442.6 402.56l-116.88-92.526z"
//       fill="#FFF"
//     ></path>
//   </svg>
// );

// const FlashIcon = () => (
//   <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
//     <path d="M0 0h24v24H0z" fill="none" />
//     <path d="M13 9h8L11 24v-9H4l9-15v9zm-2 2V7.22L7.532 13H13v4.394L17.263 11H11z" fill="rgba(0,0,255,1)" />
//   </svg>
// );
